import type { VoxelWorld } from "../core/VoxelWorld.ts";

interface VoxelEntry {
  x: number;
  y: number;
  z: number;
  blockId: string;
  blockSpec: string;
}

interface Cuboid {
  x1: number;
  y1: number;
  z1: number;
  x2: number;
  y2: number;
  z2: number;
  blockSpec: string;
}

import {
  parseBlockSpec,
  toMinecraftBlockSpec
} from "../core/blockSpec.ts";
import { getDefaultBlockState } from "../core/blockRegistry.ts";
import {
  serializeStructuredBlockEntitySnbt
} from "../core/blockEntityData.ts";

const MINECRAFT_FILL_LIMIT = 32768;

function coordinateKey(x, y, z) {
  return String(x) + "," + String(y) + "," + String(z);
}

function cuboidVolume(cuboid) {
  return (
    (cuboid.x2 - cuboid.x1 + 1) *
    (cuboid.y2 - cuboid.y1 + 1) *
    (cuboid.z2 - cuboid.z1 + 1)
  );
}

function splitCuboidToFillLimit(cuboid: Cuboid): Cuboid[] {
  if (cuboidVolume(cuboid) <= MINECRAFT_FILL_LIMIT) {
    return [cuboid];
  }

  const lengths = {
    x: cuboid.x2 - cuboid.x1 + 1,
    y: cuboid.y2 - cuboid.y1 + 1,
    z: cuboid.z2 - cuboid.z1 + 1
  };

  const axis = Object.keys(lengths).sort(function (firstAxis, secondAxis) {
    return lengths[secondAxis] - lengths[firstAxis];
  })[0];

  const minimumField = axis + "1";
  const maximumField = axis + "2";
  const middle = Math.floor((cuboid[minimumField] + cuboid[maximumField]) / 2);

  const firstHalf = { ...cuboid, [maximumField]: middle };
  const secondHalf = { ...cuboid, [minimumField]: middle + 1 };

  return [
    ...splitCuboidToFillLimit(firstHalf),
    ...splitCuboidToFillLimit(secondHalf)
  ];
}

function createGreedyCuboids(entriesForBlock: VoxelEntry[], blockSpec: string): Cuboid[] {
  const available = new Set(entriesForBlock.map(function (entry) {
    return coordinateKey(entry.x, entry.y, entry.z);
  }));

  const sortedEntries = entriesForBlock.slice().sort(function (a, b) {
    return a.y - b.y || a.z - b.z || a.x - b.x;
  });

  const cuboids: Cuboid[] = [];

  sortedEntries.forEach(function (start) {
    const startKey = coordinateKey(start.x, start.y, start.z);
    if (!available.has(startKey)) {
      return;
    }

    let x2 = start.x;
    while (available.has(coordinateKey(x2 + 1, start.y, start.z))) {
      x2 += 1;
    }

    let z2 = start.z;
    let canExtendZ = true;
    while (canExtendZ) {
      const candidateZ = z2 + 1;
      for (let x = start.x; x <= x2; x += 1) {
        if (!available.has(coordinateKey(x, start.y, candidateZ))) {
          canExtendZ = false;
          break;
        }
      }
      if (canExtendZ) {
        z2 = candidateZ;
      }
    }

    let y2 = start.y;
    let canExtendY = true;
    while (canExtendY) {
      const candidateY = y2 + 1;
      for (let z = start.z; z <= z2 && canExtendY; z += 1) {
        for (let x = start.x; x <= x2; x += 1) {
          if (!available.has(coordinateKey(x, candidateY, z))) {
            canExtendY = false;
            break;
          }
        }
      }
      if (canExtendY) {
        y2 = candidateY;
      }
    }

    for (let y = start.y; y <= y2; y += 1) {
      for (let z = start.z; z <= z2; z += 1) {
        for (let x = start.x; x <= x2; x += 1) {
          available.delete(coordinateKey(x, y, z));
        }
      }
    }

    cuboids.push({
      x1: start.x,
      y1: start.y,
      z1: start.z,
      x2,
      y2,
      z2,
      blockSpec
    });
  });

  return cuboids;
}

export function optimizeWorldToCuboids(world: VoxelWorld): Cuboid[] {
  const groupedByBlock = new Map<string, VoxelEntry[]>();

  world.entries().forEach(function (entry) {
    const blockSpec = entry.blockSpec || entry.blockId;
    const group = groupedByBlock.get(blockSpec);
    if (group) {
      group.push(entry);
    } else {
      groupedByBlock.set(blockSpec, [entry]);
    }
  });

  const allCuboids: Cuboid[] = [];
  groupedByBlock.forEach(function (entriesForBlock, blockSpec) {
    const greedyCuboids = createGreedyCuboids(entriesForBlock, blockSpec);
    greedyCuboids.forEach(function (cuboid) {
      splitCuboidToFillLimit(cuboid).forEach(function (safeCuboid) {
        allCuboids.push(safeCuboid);
      });
    });
  });

  return allCuboids.sort(function (a, b) {
    return a.y1 - b.y1 || a.z1 - b.z1 || a.x1 - b.x1;
  });
}

function shiftCuboid(cuboid: Cuboid, origin: {x:number;y:number;z:number}): Cuboid {
  return {
    ...cuboid,
    x1: cuboid.x1 + origin.x,
    y1: cuboid.y1 + origin.y,
    z1: cuboid.z1 + origin.z,
    x2: cuboid.x2 + origin.x,
    y2: cuboid.y2 + origin.y,
    z2: cuboid.z2 + origin.z
  };
}

function cuboidToCommand(cuboid: Cuboid, prefix: string): string {
  const parsed = parseBlockSpec(cuboid.blockSpec);
  if (!parsed) {
    throw new Error("Cannot export an air cuboid.");
  }
  const blockName = toMinecraftBlockSpec({
    blockId: parsed.blockId,
    state: {
      ...getDefaultBlockState(parsed.blockId),
      ...parsed.state
    }
  });
  const singleBlock =
    cuboid.x1 === cuboid.x2 &&
    cuboid.y1 === cuboid.y2 &&
    cuboid.z1 === cuboid.z2;

  if (singleBlock) {
    return (prefix || "") +
      "setblock " +
      cuboid.x1 + " " + cuboid.y1 + " " + cuboid.z1 + " " +
      blockName + " replace";
  }

  return (prefix || "") +
    "fill " +
    cuboid.x1 + " " + cuboid.y1 + " " + cuboid.z1 + " " +
    cuboid.x2 + " " + cuboid.y2 + " " + cuboid.z2 + " " +
    blockName + " replace";
}

function createBlockEntityCommands(
  world: VoxelWorld,
  origin: { x: number; y: number; z: number }
): string[] {
  const commands: string[] = [];

  world.entries().forEach((entry) => {
    if (!entry.blockEntity) return;

    const x = entry.x + origin.x;
    const y = entry.y + origin.y;
    const z = entry.z + origin.z;
    const structured = serializeStructuredBlockEntitySnbt(
      entry.blockEntity,
      world.minecraftVersion
    );

    if (structured) {
      commands.push(
        "data merge block " +
          x + " " + y + " " + z + " " +
          structured
      );
    }

    const raw = entry.blockEntity.rawSnbt?.trim();
    if (raw) {
      const compound = raw.startsWith("{")
        ? raw
        : "{" + raw + "}";
      commands.push(
        "data merge block " +
          x + " " + y + " " + z + " " +
          compound
      );
    }
  });

  return commands;
}

export function createMinecraftExport(world, format, originInput) {
  const origin = {
    x: Number(originInput.x) || 0,
    y: Number(originInput.y) || 0,
    z: Number(originInput.z) || 0
  };

  if (format === "json") {
    const project = world.toJSON();
    project.origin = origin;
    return {
      commandCount: 0,
      text: JSON.stringify(project, null, 2)
    };
  }

  const cuboids = optimizeWorldToCuboids(world).map(function (cuboid) {
    return shiftCuboid(cuboid, origin);
  });
  const blockCommands = cuboids.map(function (cuboid) {
    return cuboidToCommand(cuboid, "");
  });
  const blockEntityCommands = createBlockEntityCommands(
    world,
    origin
  );
  const rawCommands = blockCommands.concat(blockEntityCommands);

  if (format === "commands") {
    return {
      commandCount: rawCommands.length,
      text: rawCommands.map(function (command) {
        return "/" + command;
      }).join("\n")
    };
  }

  if (format === "skript") {
    const skriptLines = [
      "command /build_voxel_design:",
      "    permission: voxelstudio.build",
      "    trigger:",
      "        send \"&aVoxel Studio build started.\" to player"
    ];

    rawCommands.forEach(function (command) {
      skriptLines.push(
        "        execute console command \"execute in minecraft:overworld run " +
        command.replaceAll("\"", "\\\"") +
        "\""
      );
    });

    skriptLines.push(
      "        send \"&aBuild finished: " + rawCommands.length + " optimized commands.\" to player"
    );

    return {
      commandCount: rawCommands.length,
      text: skriptLines.join("\n")
    };
  }

  const header = [
    "# Generated by Minecraft World Designer · Voxel Studio",
    "# Minecraft Java: " + (world.minecraftVersion || "unspecified"),
    "# Blocks: " + world.blocks.size,
    "# Optimized block commands: " + blockCommands.length,
    "# Block Entity commands: " + blockEntityCommands.length,
    "# Total commands: " + rawCommands.length,
    ""
  ];

  return {
    commandCount: rawCommands.length,
    text: header.concat(rawCommands).join("\n")
  };
}
